import { effect, inject, provideAppInitializer } from '@angular/core';
import { provideTransloco, TranslocoService } from '@jsverse/transloco';
import { firstValueFrom } from 'rxjs';

import { AuthStore } from '@mb/auth/client';
import { isBrowser } from '@mb/common/client';

import { TranslocoHttpLoader } from '../transloco-loader';
import { translocoConfig } from '../transloco.config';
import {
  isWorkforceLang,
  resolveBrowserLang,
  WORKFORCE_DEFAULT_LANG,
} from '../i18n/workforce-langs';
import { provideWorkforceAppLocale } from './app-locale.providers';

/**
 * The language the app starts in, before any session is known.
 *
 * The browser's preferences are the only signal available at this point — on
 * the server there are none, so the render starts in the default language and
 * the browser corrects it during hydration.
 */
function initialLang() {
  if (!isBrowser()) {
    return WORKFORCE_DEFAULT_LANG;
  }
  return resolveBrowserLang(navigator.languages ?? [navigator.language]);
}

export function provideAppI18n() {
  return [
    provideTransloco({
      config: translocoConfig,
      loader: TranslocoHttpLoader,
    }),
    ...provideWorkforceAppLocale(),
    provideAppInitializer(() => {
      const transloco = inject(TranslocoService);
      const authStore = inject(AuthStore);

      const lang = initialLang();
      transloco.setActiveLang(lang);

      // A stored choice outranks the browser, so it is applied as soon as the
      // session arrives — which is after this initializer has already resolved.
      effect(() => {
        const stored = authStore.session()?.user?.language;
        if (isWorkforceLang(stored) && stored !== transloco.getActiveLang()) {
          transloco.setActiveLang(stored);
        }
      });

      return firstValueFrom(transloco.load(lang));
    }),
  ];
}
